import React, { useRef, useState, useEffect } from "react";
import { Prompt } from "react-router-dom";
import { get, put, del } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { FormGroup, FormControl, ControlLabel, Button } from "react-bootstrap";
import LoaderButton from "../components/LoaderButton";
import PasswordGenerator from "../components/PasswordGenerator";
import PasswordStrengthIndicator from "../components/PasswordStrengthIndicator";
import config from "../config";
import "./Notes.css";
import { s3Upload } from "../libs/awsLib";
import utils from "../utils";

export default function LoginDetail(props) {
    const file = useRef(null);
    const [loginDetail, setLoginDetail] = useState(null);
    const [websiteName, setwebsiteName] = useState("");
    const [loginId, setLoginId] = useState("");
    const [password, setPassword] = useState("");
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showGenerator, setShowGenerator] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    //config.ENCRYPTION_KEY

  useEffect(() => {
    async function loadLoginDetail() {
      const restOperation = get({
        apiName: "logindetails",
        path: `/logindetails/${props.match.params.id}`
      });
      const { body } = await restOperation.response;
      return await body.json();
    }

    async function onLoad() {
      try {
        console.log(props.match.params.id);
        const loginDetail = await loadLoginDetail();
        const { websiteName, loginId, password, websiteUrl, attachment } = loginDetail;

        if (attachment) {
          const urlResult = await getUrl({
            path: attachment,
            options: { accessLevel: 'private' }
          });
          loginDetail.attachmentURL = urlResult.url;
        }

        setwebsiteName(utils.decrypt(websiteName));
        setLoginId(utils.decrypt(loginId));
        setPassword(utils.decrypt(password));
        setWebsiteUrl(utils.decrypt(websiteUrl));
        setLoginDetail(loginDetail);
      } catch (e) {
        alert(e);
      }
    }

    onLoad();
  }, [props.match.params.id]);

  // Track changes to form fields
  useEffect(() => {
    if (!loginDetail) return;
    
    const originalWebsiteName = utils.decrypt(loginDetail.websiteName);
    const originalLoginId = utils.decrypt(loginDetail.loginId);
    const originalPassword = utils.decrypt(loginDetail.password);
    const originalWebsiteUrl = utils.decrypt(loginDetail.websiteUrl);
    
    const hasChanges = 
      websiteName !== originalWebsiteName ||
      loginId !== originalLoginId ||
      password !== originalPassword ||
      websiteUrl !== originalWebsiteUrl ||
      file.current?.files.length > 0;
    
    setHasUnsavedChanges(hasChanges);
  }, [websiteName, loginId, password, websiteUrl, loginDetail]);

  function validateForm() {
    return websiteName.length > 0;
  }
  
  function formatFilename(str) {
    return str.replace(/^\w+-/, "");
  }
  
  function handleFileChange(event) {
    file.current = event.target.files[0];
  }
  
  async function saveLoginDetail(loginDetail) {
    console.log(loginDetail.data);
    const restOperation = put({
      apiName: "logindetails",
      path: `/logindetails/${props.match.params.id}`,
      options: {
        body: loginDetail.data
      }
    });
    await restOperation.response;
  }
  
  async function handleSubmit(event) {
    let attachment;
  
    event.preventDefault();
  
    if (file.current && file.current.size > config.MAX_ATTACHMENT_SIZE) {
      alert(
        `Please pick a file smaller than ${config.MAX_ATTACHMENT_SIZE /
          1000000} MB.`
      );
      return;
    }
  
    setIsLoading(true);
  
    try {
      if (file.current) {
        attachment = await s3Upload(file.current);
      }

      const data = {
        websiteName: utils.encrypt(websiteName),
        loginId: utils.encrypt(loginId),
        password: utils.encrypt(password),
        websiteUrl: utils.encrypt(websiteUrl)
      }
  
      await saveLoginDetail({
        data,
        attachment: attachment || loginDetail.attachment
      });
      setHasUnsavedChanges(false);
      props.history.push("/");
    } catch (e) {
      alert(e);
      setIsLoading(false);
    }
  }
  
  async function deleteLoginDetail() {
    const restOperation = del({
      apiName: "logindetails",
      path: `/logindetails/${props.match.params.id}`
    });
    await restOperation.response;
  }
  
  async function handleDelete(event) {
    event.preventDefault();
  
    const confirmed = window.confirm(
      "Are you sure you want to delete this note?"
    );
  
    if (!confirmed) {
      return;
    }
  
    setIsDeleting(true);
  
    try {
      await deleteLoginDetail();
      setHasUnsavedChanges(false);
      props.history.push("/");
    } catch (e) {
      alert(e);
      setIsDeleting(false);
    }
  }
  
  return (
    <div className="Notes">
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes. Are you sure you want to leave?"
      />
      {loginDetail && (
        <form onSubmit={handleSubmit}>
          <FormGroup controlId="content">
          <ControlLabel>Website name</ControlLabel>
            <FormControl
              value={websiteName}
              type="text"
              onChange={e => setwebsiteName(e.target.value)}
            />
            <ControlLabel>Login id</ControlLabel>
            <FormControl
              value={loginId}
              type="text"
              onChange={e => setLoginId(e.target.value)}
            />
            <ControlLabel>Password</ControlLabel>
            <FormControl
              value={password}
              type="text"
              onChange={e => setPassword(e.target.value)}
            />
            <PasswordStrengthIndicator password={password} />
            <Button 
              bsStyle="link" 
              onClick={() => setShowGenerator(!showGenerator)}
              style={{ padding: "5px 0", marginTop: "5px" }}
            >
              {showGenerator ? "Hide" : "Show"} Password Generator
            </Button>
            {showGenerator && (
              <PasswordGenerator 
                onPasswordGenerated={(pwd) => {
                  setPassword(pwd);
                  setShowGenerator(false);
                }}
              />
            )}
          </FormGroup>
          <FormGroup controlId="url">
            <ControlLabel>Url</ControlLabel>
            <FormControl
              value={websiteUrl}
              type="text"
              onChange={e => setWebsiteUrl(e.target.value)}
            />
          </FormGroup>
          {loginDetail.attachment && (
            <FormGroup>
              <ControlLabel>Attachment</ControlLabel>
              <FormControl.Static>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={loginDetail.attachmentURL}
                >
                  {formatFilename(loginDetail.attachment)}
                </a>
              </FormControl.Static>
            </FormGroup>
          )}
          <FormGroup controlId="file">
            {!loginDetail.attachment && <ControlLabel>Attachment</ControlLabel>}
            <FormControl onChange={handleFileChange} type="file" />
          </FormGroup>
          <LoaderButton
            block
            type="submit"
            bsSize="large"
            bsStyle="primary"
            isLoading={isLoading}
            disabled={!validateForm()}
          >
            Save
          </LoaderButton>
          <LoaderButton
            block
            bsSize="large"
            bsStyle="danger"
            onClick={handleDelete}
            isLoading={isDeleting}
          >
            Delete
          </LoaderButton>
        </form>
      )}
    </div>
  );
}